/**
 * 리깅 파사드 — 지오메트리 + 실루엣 → 움직이는 SkinnedMesh + 클립 14개.
 *
 * 모든 메시를 SkinnedMesh 로 그린다. 휴머노이드가 아니어도(골렘, 슬라임) 뼈대는
 * 세우고 idle 만 자연스럽게 돈다. 이유는 둘이다: 셰이더에 USE_SKINNING 이 붙었다
 * 떨어졌다 하면 재컴파일(17초)이 나고, 게임 쪽 파이프라인은 "전부 스킨드"가 단순하다.
 */

import * as THREE from 'three';
import type { Field } from '../types.ts';
import { buildClips } from './animations.ts';
import { rigGeometry, type RigResult, type SilhouetteSampler } from './skeleton.ts';

export interface Rig extends RigResult {
  clips: THREE.AnimationClip[];
}

/** 단일 뷰 마스크에서 (s,t) 샘플러. 시트 모드는 panelSampler 가 같은 얼굴을 갖는다. */
export function maskSampler(mask: Field): SilhouetteSampler {
  const { width: w, height: h } = mask;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (mask.data[y * w + x]! < 0.5) continue;
      if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
    }
  }
  if (x1 < 0) { x0 = 0; y0 = 0; x1 = w - 1; y1 = h - 1; }
  const bw = Math.max(1, x1 - x0), bh = Math.max(1, y1 - y0);
  return {
    sample(s, t) {
      if (s < 0 || s > 1 || t < 0 || t > 1) return 0;
      const xi = Math.round(x0 + s * bw);
      const yi = Math.round(y1 - t * bh);
      if (xi < 0 || yi < 0 || xi >= w || yi >= h) return 0;
      return mask.data[yi * w + xi]!;
    },
  };
}

export function rigMesh(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  sil: SilhouetteSampler,
): Rig {
  const result = rigGeometry(geometry, material, sil);
  const clips = buildClips(result.bones, result.height, result.layout);
  result.skinned.animations = clips;
  return { ...result, clips };
}

/** 앱 시작 시 셰이더를 스킨드 경로로 컴파일시키기 위한 티끌 메시. */
export function warmupSkinnedMesh(material: THREE.Material): THREE.SkinnedMesh {
  const g = new THREE.PlaneGeometry(0.001, 0.001);
  const n = g.getAttribute('position').count;
  g.setAttribute('aShell', new THREE.Float32BufferAttribute(new Float32Array(n), 1));
  g.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(new Uint16Array(n * 4), 4));
  const w = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) w[i * 4] = 1;
  g.setAttribute('skinWeight', new THREE.Float32BufferAttribute(w, 4));
  const mesh = new THREE.SkinnedMesh(g, material);
  const bone = new THREE.Bone();
  mesh.add(bone);
  mesh.bind(new THREE.Skeleton([bone]));
  mesh.frustumCulled = false;
  return mesh;
}
